import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";

export interface MeUser {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  role: "admin" | "user";
  display_name: string | null;
  must_change_password: boolean;
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
}

export const meQueryKey = ["auth", "me"] as const;

export function useMe(enabled = true) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<MeUser>({
    queryKey: meQueryKey,
    queryFn: () => apiFetch<MeUser>("/users/me"),
    enabled: enabled && Boolean(accessToken),
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const setToken = useAuthStore((s) => s.setToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const res = await apiFetch<LoginResponse>("/auth/jwt/login", {
        method: "POST",
        auth: false,
        form: { username: vars.email, password: vars.password },
      });
      return res;
    },
    onSuccess: async (data) => {
      setToken(data.access_token);
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useLogout() {
  const clearToken = useAuthStore((s) => s.clearToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await apiFetch("/auth/jwt/logout", { method: "POST" });
      } catch {
        // Logout is best-effort: even if the server denies (already expired),
        // we still clear the local token so the user is signed out client-side.
      }
    },
    onSettled: () => {
      clearToken();
      queryClient.clear();
    },
  });
}

export function useChangeOwnPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { current_password: string; new_password: string }) =>
      apiFetch<void>("/users/me/change-password", {
        method: "POST",
        body: vars,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useUpdateOwnProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { display_name: string | null }) =>
      apiFetch<MeUser>("/users/me", { method: "PATCH", body: vars }),
    onSuccess: (data) => {
      queryClient.setQueryData(meQueryKey, data);
    },
  });
}
